import React, { useEffect } from "react";
import {
  FormFrameworkComponentType,
  CashFlowType,
  InstrumentType,
} from "../../../../types/Enums";
import { useAtomValue } from "jotai";
import { AccountAtom, TransactionsAtom, InstrumentStateAtom } from "../../../../store";
import { createCashFlow } from "../../../../api/Cashflow";
import { useTransactions } from "../../../../hooks/useTransactions";
import { Currency } from "../../../../types/Currencies";
import { delay } from "../../../../utils";
import moment from "moment";

interface AddCashFlowFormController {
  isLoading: boolean;
  schema: any;
  submitPayload: (payload: any) => void;
}

export const useAddCashFlowFormController = (props: any) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const accountState = useAtomValue(AccountAtom);
  const transactionsState = useAtomValue(TransactionsAtom);
  const instrumentState = useAtomValue(InstrumentStateAtom);
  const transactions = useTransactions();

  const [schema, setSchema] = React.useState<any>({
    date: {
      value: moment().format("YYYY-MM-DD"),
      label: "Date",
      error: "",
      type: FormFrameworkComponentType.Date,
      required: true,
    },
    accountId: {
      value: accountState?.selectedAccount?.id,
    },
    type: {
      value: "Deposit",
      label: "Category",
      error: "",
      type: FormFrameworkComponentType.Select,
      enum: Object.values(CashFlowType),
      required: true,
    },
    instrumentId: {
      value: "",
      label: "Underlying Instrument",
      error: "",
      type: FormFrameworkComponentType.InstrumentSearch,
      instrumentType: InstrumentType.Equity,
      required: true,
      enableCreateNew: true,
    },
  
    value: {
      value: 0,
      label: "Amount",
      error: "",
      type: FormFrameworkComponentType.Number,
      required: true,
    },
    currency: {
      value: "USD",
      label: "Currency",
      error: "",
      type: FormFrameworkComponentType.Select,
      required: true,
      enum: Object.values(Currency),
      enableCreateNew: false,
    },
    description: {
      value: "",
      label: "Description",
      error: "",
      type: FormFrameworkComponentType.Text,
      required: true,
    },
    fees: {
      value: 0,
      label: "Fees",
      error: "",
      type: FormFrameworkComponentType.Number,
      required: false,
    },
  
  });

  const submitPayload = async (payload: any) => {
    setIsLoading(true);
    const parsePayloadToJustValues = Object.keys(payload).reduce((acc: any, key) => {
      if (key === "instrumentId" || key === "underlyingInstrumentId") {
        // Handle instrument search values by looking up in InstrumentStateAtom
        if (payload[key].value && payload[key].value.value) {
          const instrumentSearchValue = payload[key].value.value;
          // Look up the instrument in the state atom
          const foundInstrument = instrumentState.find(instrument => 
            instrument.symbol === instrumentSearchValue || 
            instrument.name === instrumentSearchValue ||
            instrument.id === instrumentSearchValue
          );
          acc[key] = foundInstrument?.id || null;
        } else {
          acc[key] = null;
        }

        return acc;
      }
      acc[key] = payload[key].value;
      return acc;
    }, {});



    const CashFlow = {
      ...parsePayloadToJustValues,
      accountId: accountState?.selectedAccount?.id,
    };

    const createdCashFlow = await createCashFlow(
      CashFlow,
      accountState?.selectedAccount?.id
    );

    if (createdCashFlow) {
      if (createdCashFlow.success === true) {
        delay(1000);
        await transactions.getTradesAndCashflows(
          accountState?.selectedAccount?.id
        );

        props.onComplete();
      } else {
        const errors = createdCashFlow.errors || {};
        const newSchema = { ...schema };
        Object.keys(errors).forEach((key) => {
          if (newSchema[key]) {
            newSchema[key].error = errors[key];
          }
        });
        setSchema(newSchema);

        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    schema,
    submitPayload,
  } as AddCashFlowFormController;
}; 